import { useState, useMemo } from "react";
import { Layout } from "@/components/Layout";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Switch } from "@/components/ui/switch";
import {
  Eye,
  Monitor,
  Projector,
  Info,
  AlertTriangle,
  CheckCircle2,
  Lightbulb,
} from "lucide-react";

// --------- constants ---------
const ASPECT_RATIOS = [
  { value: "16:9", w: 16, h: 9 },
  { value: "16:10", w: 16, h: 10 },
  { value: "4:3", w: 4, h: 3 },
  { value: "21:9", w: 21, h: 9 },
  { value: "1:1", w: 1, h: 1 },
];

const CONTENT_TYPES = [
  {
    value: "text",
    label: "Detailed Text / Spreadsheets",
    multiplier: 4,
    blurb: "4H rule — max distance = 4× screen height. Suitable for fine text, financials, code.",
  },
  {
    value: "general",
    label: "General Presentation (slides + graphics)",
    multiplier: 6,
    blurb: "6H rule — max distance = 6× screen height. Standard for keynotes & conferences.",
  },
  {
    value: "video",
    label: "Video / Image Only",
    multiplier: 8,
    blurb: "8H rule — max distance = 8× screen height. For cinematic content, no detailed text.",
  },
];

// Common LED pixel pitches (mm). Min viewing distance ≈ pitch in metres.
const PIXEL_PITCHES = [
  { value: "1.5", label: "P1.5 — Indoor close-up (control rooms, broadcast)" },
  { value: "1.9", label: "P1.9 — Indoor premium" },
  { value: "2.6", label: "P2.6 — Indoor general (popular)" },
  { value: "2.9", label: "P2.9 — Indoor / stage backdrop" },
  { value: "3.9", label: "P3.9 — Stage / hire" },
  { value: "4.8", label: "P4.8 — Stage / large venue" },
  { value: "6.25", label: "P6.25 — Outdoor / arena" },
  { value: "10", label: "P10 — Outdoor billboard" },
];

const MAX_OFF_AXIS_DEG = 30; // THX recommended max horizontal viewing angle

// Recommended projector lumens per square metre of screen area for given ambient light.
// Source: industry "ANSI lumens / m²" rule of thumb (InfoComm-style).
const AMBIENT_LUMENS = [
  { value: "dark", label: "Dark Room (cinema, controlled)", lumensPerSqM: 600 },
  { value: "dim", label: "Dim (mood lighting, evening event)", lumensPerSqM: 1500 },
  { value: "standard", label: "Standard Indoor (offices, conference)", lumensPerSqM: 2500 },
  { value: "bright", label: "Bright (windows, daytime, ballroom)", lumensPerSqM: 4000 },
  { value: "outdoor", label: "Outdoor / Daylight (very challenging)", lumensPerSqM: 7000 },
];

// ---- helpers ----
const round = (n, decimals = 2) => Math.round(n * 10 ** decimals) / 10 ** decimals;
const mToFt = (m) => m * 3.28084;
const ftToM = (ft) => ft / 3.28084;

const AVSpecs = () => {
  const [isMetric, setIsMetric] = useState(true);
  const [screenType, setScreenType] = useState("projector"); // projector | led
  const [aspectRatio, setAspectRatio] = useState("16:9");
  const [screenWidth, setScreenWidth] = useState("4"); // metres or feet
  const [pixelPitch, setPixelPitch] = useState("2.6");
  const [contentType, setContentType] = useState("general");
  const [frontRow, setFrontRow] = useState("3");
  const [backRow, setBackRow] = useState("20");
  const [audienceWidth, setAudienceWidth] = useState("12");
  const [ambient, setAmbient] = useState("standard");

  const unit = isMetric ? "m" : "ft";

  // Convert user inputs to metres for internal maths
  const toM = (v) => {
    const num = parseFloat(v);
    if (isNaN(num)) return 0;
    return isMetric ? num : ftToM(num);
  };
  const fromM = (m) => (isMetric ? round(m, 2) : round(mToFt(m), 2));

  const calc = useMemo(() => {
    const aspect = ASPECT_RATIOS.find((a) => a.value === aspectRatio);
    const content = CONTENT_TYPES.find((c) => c.value === contentType);

    const widthM = toM(screenWidth);
    const heightM = aspect ? (widthM * aspect.h) / aspect.w : 0;
    const diagonalM = Math.sqrt(widthM ** 2 + heightM ** 2);
    const diagonalInches = diagonalM * 39.3701;

    // Min viewing distance
    let minDistM;
    if (screenType === "led") {
      // Rule of thumb: pixel pitch (mm) × 1 m
      minDistM = parseFloat(pixelPitch);
    } else {
      // Projector / classic AV — 1.5× screen height is the comfort floor
      minDistM = heightM * 1.5;
    }

    // Max legible distance based on the 4H / 6H / 8H rule
    const maxDistM = heightM * (content?.multiplier ?? 6);

    // User-entered rows / audience
    const frontM = toM(frontRow);
    const backM = toM(backRow);
    const audienceHalfM = toM(audienceWidth) / 2;

    // Horizontal off-axis angle at front row (worst case — most extreme angle)
    // angle = atan(audienceHalf / frontDistance)
    const frontAngleDeg = frontM > 0
      ? Math.atan(audienceHalfM / frontM) * (180 / Math.PI)
      : 0;
    // Angle at back row (gentler)
    const backAngleDeg = backM > 0
      ? Math.atan(audienceHalfM / backM) * (180 / Math.PI)
      : 0;

    // Status flags
    const frontTooClose = frontM > 0 && frontM < minDistM;
    const backTooFar = backM > 0 && backM > maxDistM;
    const sidesOffAxis = frontAngleDeg > MAX_OFF_AXIS_DEG;
    const allGood = !frontTooClose && !backTooFar && !sidesOffAxis;

    // Suggested screen width for given content + back row distance
    // maxDistM = height * mult => height = maxDist / mult
    // width = height * aspect.w / aspect.h
    const suggestedHeightM = backM / (content?.multiplier ?? 6);
    const suggestedWidthM = aspect ? (suggestedHeightM * aspect.w) / aspect.h : 0;

    // Projector brightness (lumens) — only meaningful when screenType === "projector"
    const screenAreaM2 = widthM * heightM;
    const ambientPreset = AMBIENT_LUMENS.find((a) => a.value === ambient);
    const recommendedLumens = ambientPreset
      ? Math.round(screenAreaM2 * ambientPreset.lumensPerSqM)
      : 0;

    return {
      aspect,
      content,
      widthM,
      heightM,
      diagonalM,
      diagonalInches,
      minDistM,
      maxDistM,
      frontM,
      backM,
      audienceHalfM,
      frontAngleDeg,
      backAngleDeg,
      frontTooClose,
      backTooFar,
      sidesOffAxis,
      allGood,
      suggestedWidthM,
      suggestedHeightM,
      screenAreaM2,
      ambientPreset,
      recommendedLumens,
    };
  }, [aspectRatio, contentType, screenType, screenWidth, pixelPitch, frontRow, backRow, audienceWidth, isMetric, ambient]);

  return (
    <Layout
      title="AV Tech Specs"
      subtitle="Sightline check, screen sizing, off-axis verification, and projector brightness — for projectors and LED walls"
    >
      <div className="max-w-6xl mx-auto space-y-6">
        {/* Info card */}
        <Card className="tactical-card">
          <CardContent className="p-4">
            <div className="flex items-start gap-3">
              <Info className="h-5 w-5 mt-0.5 flex-shrink-0" />
              <p className="text-sm text-muted-foreground">
                Applies the industry-standard <strong>4H/6H/8H rule</strong> for max legible viewing distance,
                THX <strong>±30° off-axis</strong> recommendation, and (for LED walls) the
                <strong> pixel-pitch ≈ min-distance</strong> rule of thumb. Use it to verify a projector
                size or to confirm an LED wall&apos;s pitch is fit for the venue.
              </p>
            </div>
          </CardContent>
        </Card>

        {/* Inputs */}
        <Card className="tactical-card">
          <CardHeader>
            <CardTitle className="flex items-center gap-2 text-lg uppercase">
              <Eye className="h-5 w-5" />
              Setup
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-6">
            {/* Top toggles */}
            <div className="grid md:grid-cols-2 gap-4">
              {/* Screen type */}
              <div className="flex items-center justify-between p-3 bg-secondary/50 rounded-sm">
                <span className="text-sm font-medium flex items-center gap-2">
                  {screenType === "projector" ? <Projector className="h-4 w-4" /> : <Monitor className="h-4 w-4" />}
                  Screen Type
                </span>
                <div className="flex items-center gap-3">
                  <span className={`text-sm ${screenType === "projector" ? "text-foreground" : "text-muted-foreground"}`}>
                    Projector
                  </span>
                  <Switch
                    checked={screenType === "led"}
                    onCheckedChange={(v) => setScreenType(v ? "led" : "projector")}
                    data-testid="screen-type-toggle"
                  />
                  <span className={`text-sm ${screenType === "led" ? "text-foreground" : "text-muted-foreground"}`}>
                    LED Wall
                  </span>
                </div>
              </div>

              {/* Unit */}
              <div className="flex items-center justify-between p-3 bg-secondary/50 rounded-sm">
                <span className="text-sm font-medium">Unit System</span>
                <div className="flex items-center gap-3">
                  <span className={`text-sm ${!isMetric ? "text-foreground" : "text-muted-foreground"}`}>Imperial (ft)</span>
                  <Switch checked={isMetric} onCheckedChange={setIsMetric} data-testid="unit-toggle" />
                  <span className={`text-sm ${isMetric ? "text-foreground" : "text-muted-foreground"}`}>Metric (m)</span>
                </div>
              </div>
            </div>

            {/* Screen dimensions */}
            <div className="grid md:grid-cols-3 gap-4">
              <div className="space-y-2">
                <Label className="label-tactical">Aspect Ratio</Label>
                <Select value={aspectRatio} onValueChange={setAspectRatio}>
                  <SelectTrigger className="tactical-input" data-testid="aspect-ratio-select">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {ASPECT_RATIOS.map((a) => (
                      <SelectItem key={a.value} value={a.value}>
                        {a.value}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              <div className="space-y-2">
                <Label className="label-tactical" htmlFor="screen-width">
                  Screen Width ({unit})
                </Label>
                <Input
                  id="screen-width"
                  type="number"
                  min="0.1"
                  step="0.1"
                  value={screenWidth}
                  onChange={(e) => setScreenWidth(e.target.value)}
                  className="tactical-input"
                  data-testid="screen-width-input"
                />
                <p className="text-[10px] text-muted-foreground">
                  Height auto-derived from aspect → {fromM(calc.heightM)} {unit} • Diagonal{" "}
                  {round(calc.diagonalInches, 1)}″
                </p>
              </div>

              {screenType === "led" ? (
                <div className="space-y-2">
                  <Label className="label-tactical">Pixel Pitch</Label>
                  <Select value={pixelPitch} onValueChange={setPixelPitch}>
                    <SelectTrigger className="tactical-input" data-testid="pixel-pitch-select">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {PIXEL_PITCHES.map((p) => (
                        <SelectItem key={p.value} value={p.value}>
                          {p.label}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                  <p className="text-[10px] text-muted-foreground">
                    Min viewing distance ≈ {pixelPitch} m (pitch-mm rule)
                  </p>
                </div>
              ) : (
                <div className="space-y-2">
                  <Label className="label-tactical">Content Type</Label>
                  <Select value={contentType} onValueChange={setContentType}>
                    <SelectTrigger className="tactical-input" data-testid="content-type-select">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {CONTENT_TYPES.map((c) => (
                        <SelectItem key={c.value} value={c.value}>
                          {c.label}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              )}
            </div>

            {/* Content type when LED (show alongside) */}
            {screenType === "led" && (
              <div className="space-y-2">
                <Label className="label-tactical">Content Type</Label>
                <Select value={contentType} onValueChange={setContentType}>
                  <SelectTrigger className="tactical-input" data-testid="content-type-select-led">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {CONTENT_TYPES.map((c) => (
                      <SelectItem key={c.value} value={c.value}>
                        {c.label}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                <p className="text-[10px] text-muted-foreground">{calc.content?.blurb}</p>
              </div>
            )}

            {screenType === "projector" && (
              <p className="text-[10px] text-muted-foreground">{calc.content?.blurb}</p>
            )}

            {/* Room layout */}
            <div className="grid md:grid-cols-3 gap-4 pt-4 border-t border-border/40">
              <div className="space-y-2">
                <Label className="label-tactical" htmlFor="front-row">
                  Front Row Distance ({unit})
                </Label>
                <Input
                  id="front-row"
                  type="number"
                  min="0"
                  step="0.1"
                  value={frontRow}
                  onChange={(e) => setFrontRow(e.target.value)}
                  className="tactical-input"
                  data-testid="front-row-input"
                />
                <p className="text-[10px] text-muted-foreground">From screen to first row of seats</p>
              </div>
              <div className="space-y-2">
                <Label className="label-tactical" htmlFor="back-row">
                  Back Row Distance ({unit})
                </Label>
                <Input
                  id="back-row"
                  type="number"
                  min="0"
                  step="0.1"
                  value={backRow}
                  onChange={(e) => setBackRow(e.target.value)}
                  className="tactical-input"
                  data-testid="back-row-input"
                />
                <p className="text-[10px] text-muted-foreground">From screen to back row of seats</p>
              </div>
              <div className="space-y-2">
                <Label className="label-tactical" htmlFor="audience-width">
                  Audience Width ({unit})
                </Label>
                <Input
                  id="audience-width"
                  type="number"
                  min="0"
                  step="0.1"
                  value={audienceWidth}
                  onChange={(e) => setAudienceWidth(e.target.value)}
                  className="tactical-input"
                  data-testid="audience-width-input"
                />
                <p className="text-[10px] text-muted-foreground">Total left-to-right extent of seating</p>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Results: KPI cards */}
        <div className="grid md:grid-cols-3 gap-4" data-testid="sightline-kpis">
          <KpiCard
            label="Min Viewing Distance"
            value={`${fromM(calc.minDistM)} ${unit}`}
            sub={screenType === "led" ? `P${pixelPitch} pitch rule` : "1.5× screen height"}
            tone="info"
            testId="kpi-min-distance"
          />
          <KpiCard
            label="Max Legible Distance"
            value={`${fromM(calc.maxDistM)} ${unit}`}
            sub={`${calc.content?.multiplier}× screen height (${calc.content?.label.split(" ")[0]})`}
            tone="info"
            testId="kpi-max-distance"
          />
          <KpiCard
            label="Max Off-Axis Angle"
            value={`${round(calc.frontAngleDeg, 1)}°`}
            sub={`Limit: ±${MAX_OFF_AXIS_DEG}° (THX). Back row: ${round(calc.backAngleDeg, 1)}°`}
            tone={calc.sidesOffAxis ? "warn" : "ok"}
            testId="kpi-off-axis"
          />
        </div>

        {/* Status + suggested fix */}
        <Card className="tactical-card" data-testid="sightline-status">
          <CardHeader>
            <CardTitle className="flex items-center gap-2 text-lg uppercase">
              {calc.allGood ? <CheckCircle2 className="h-5 w-5" /> : <AlertTriangle className="h-5 w-5" />}
              {calc.allGood ? "Sightlines Look Good" : "Issues Detected"}
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-3">
            <StatusRow
              ok={!calc.frontTooClose}
              okText={`Front row at ${fromM(calc.frontM)} ${unit} is ≥ minimum (${fromM(calc.minDistM)} ${unit})`}
              badText={
                screenType === "led"
                  ? `Front row (${fromM(calc.frontM)} ${unit}) is closer than the pitch-based minimum (${fromM(
                      calc.minDistM
                    )} ${unit}). Guests will see individual pixels. Move them back or use a tighter pitch.`
                  : `Front row (${fromM(calc.frontM)} ${unit}) is less than 1.5× screen height (${fromM(
                      calc.minDistM
                    )} ${unit}). Neck strain & visible pixels likely.`
              }
            />
            <StatusRow
              ok={!calc.backTooFar}
              okText={`Back row at ${fromM(calc.backM)} ${unit} is within the ${calc.content?.multiplier}H legibility limit`}
              badText={`Back row (${fromM(calc.backM)} ${unit}) exceeds the ${calc.content?.multiplier}H limit (${fromM(
                calc.maxDistM
              )} ${unit}). Recommended screen width: ${fromM(calc.suggestedWidthM)} ${unit} (height ${fromM(
                calc.suggestedHeightM
              )} ${unit}).`}
            />
            <StatusRow
              ok={!calc.sidesOffAxis}
              okText={`Worst off-axis angle (${round(calc.frontAngleDeg, 1)}°) is within ±${MAX_OFF_AXIS_DEG}°`}
              badText={`Side seats reach ${round(
                calc.frontAngleDeg,
                1
              )}° — beyond the THX ±${MAX_OFF_AXIS_DEG}° comfort cone. Consider moving front rows further from the screen or narrowing the seating block.`}
            />
          </CardContent>
        </Card>

        {/* Projector brightness — only for projector mode */}
        {screenType === "projector" && (
          <Card className="tactical-card" data-testid="projector-lumens-card">
            <CardHeader>
              <CardTitle className="flex items-center gap-2 text-lg uppercase">
                <Lightbulb className="h-5 w-5" />
                Projector Brightness
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="space-y-2">
                <Label className="label-tactical">Ambient Light Condition</Label>
                <Select value={ambient} onValueChange={setAmbient}>
                  <SelectTrigger className="tactical-input" data-testid="ambient-light-select">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {AMBIENT_LUMENS.map((a) => (
                      <SelectItem key={a.value} value={a.value}>
                        {a.label}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              <div className="grid sm:grid-cols-2 gap-3">
                <div className="p-4 bg-secondary/40 rounded-sm border border-border">
                  <p className="label-tactical">Screen Area</p>
                  <p className="text-2xl font-mono font-bold mt-1">
                    {round(calc.screenAreaM2, 2)} m²
                  </p>
                  <p className="text-xs text-muted-foreground mt-1">
                    {fromM(calc.widthM)} × {fromM(calc.heightM)} {unit}
                  </p>
                </div>
                <div
                  className="p-4 rounded-sm border"
                  style={{
                    backgroundColor: "rgba(224,122,95,0.08)",
                    borderColor: "rgba(224,122,95,0.5)",
                  }}
                  data-testid="kpi-lumens"
                >
                  <p className="label-tactical">Recommended Brightness</p>
                  <p
                    className="text-3xl font-mono font-bold mt-1"
                    style={{ color: "#E07A5F" }}
                  >
                    {calc.recommendedLumens.toLocaleString()} <span className="text-base">ANSI lumens</span>
                  </p>
                  <p className="text-xs text-muted-foreground mt-1">
                    {calc.ambientPreset?.lumensPerSqM} lumens/m² × {round(calc.screenAreaM2, 2)} m²
                  </p>
                </div>
              </div>

              <p className="text-xs text-muted-foreground">
                Quote at least this brightness. Round up to the next common projector tier
                (e.g., 3,500 / 5,000 / 7,500 / 10,000 / 15,000+ lumens) for a comfortable margin.
              </p>
            </CardContent>
          </Card>
        )}

        {/* Top-down diagram */}
        <Card className="tactical-card">
          <CardHeader>
            <CardTitle className="flex items-center gap-2 text-lg uppercase">
              <Eye className="h-5 w-5" />
              Top-down View
            </CardTitle>
          </CardHeader>
          <CardContent>
            <SightlineDiagram calc={calc} unit={unit} fromM={fromM} />
            <div className="mt-4 flex flex-wrap gap-4 text-xs">
              <LegendDot color="#81B29A" label="Optimal viewing zone" />
              <LegendDot color="#D8E8DA" label="Acceptable" border />
              <LegendDot color="#E07A5F" label="Too close / off-axis" />
              <LegendDot color="#1E2022" label="Screen" />
            </div>
          </CardContent>
        </Card>
      </div>
    </Layout>
  );
};

// ---------- Subcomponents ----------

const KpiCard = ({ label, value, sub, tone, testId }) => {
  const toneStyles = {
    ok: { color: "#81B29A", bg: "rgba(129,178,154,0.08)", border: "rgba(129,178,154,0.5)" },
    warn: { color: "#E07A5F", bg: "rgba(224,122,95,0.08)", border: "rgba(224,122,95,0.5)" },
    info: { color: undefined, bg: undefined, border: "hsl(var(--border))" },
  }[tone || "info"];
  return (
    <div
      className="p-4 rounded-sm border"
      style={{
        backgroundColor: toneStyles.bg,
        borderColor: toneStyles.border,
      }}
      data-testid={testId}
    >
      <p className="label-tactical">{label}</p>
      <p className="text-3xl font-mono font-bold mt-1" style={{ color: toneStyles.color }}>
        {value}
      </p>
      <p className="text-xs text-muted-foreground mt-2">{sub}</p>
    </div>
  );
};

const StatusRow = ({ ok, okText, badText }) => (
  <div className="flex items-start gap-3">
    {ok ? (
      <CheckCircle2 className="h-5 w-5 mt-0.5 flex-shrink-0" style={{ color: "#81B29A" }} />
    ) : (
      <AlertTriangle className="h-5 w-5 mt-0.5 flex-shrink-0" style={{ color: "#E07A5F" }} />
    )}
    <p className="text-sm leading-relaxed">{ok ? okText : badText}</p>
  </div>
);

const LegendDot = ({ color, label, border = false }) => (
  <div className="flex items-center gap-2">
    <span
      className="inline-block h-3 w-3 rounded-sm"
      style={{
        backgroundColor: color,
        border: border ? "1px solid hsl(var(--border))" : undefined,
      }}
    />
    <span className="text-muted-foreground">{label}</span>
  </div>
);

// Top-down room diagram (SVG, palette-strict)
const SightlineDiagram = ({ calc, unit, fromM }) => {
  // Visualisation bounds: pick a sensible scale based on inputs
  const maxDistShown = Math.max(calc.backM * 1.1, calc.maxDistM * 1.05, 1);
  const maxWidthShown = Math.max(calc.audienceHalfM * 2.5, calc.widthM * 1.5, 3);

  const SVG_W = 880;
  const SVG_H = 460;
  const PAD = 30;

  // Scaling: world (m) → svg px
  const scaleX = (SVG_W - PAD * 2) / maxWidthShown;
  const scaleY = (SVG_H - PAD * 2 - 30) / maxDistShown; // leave room for screen at top
  const scale = Math.min(scaleX, scaleY);

  const cx = SVG_W / 2;
  const screenY = PAD;
  const screenPx = calc.widthM * scale;

  // Map a metres (forward Y from screen, X from centre) to SVG coords
  const project = (xM, yM) => ({
    x: cx + xM * scale,
    y: screenY + yM * scale,
  });

  // Helpers for zones
  const minPx = calc.minDistM * scale;
  const maxPx = calc.maxDistM * scale;
  const conePx = Math.tan((30 * Math.PI) / 180); // tan(30°)

  // Audience block as a rect
  const front = project(-calc.audienceHalfM, calc.frontM);
  const back = project(calc.audienceHalfM, calc.backM);

  // 30° cone end points at back of view
  const coneRange = maxDistShown;
  const cone30L = project(-coneRange * conePx, coneRange);
  const cone30R = project(coneRange * conePx, coneRange);

  return (
    <div className="overflow-x-auto">
      <svg
        viewBox={`0 0 ${SVG_W} ${SVG_H}`}
        className="w-full h-auto"
        data-testid="sightline-diagram"
        style={{ maxHeight: 520 }}
      >
        {/* Room canvas */}
        <rect x="0" y="0" width={SVG_W} height={SVG_H} fill="hsl(var(--background))" />

        {/* Optimal arc band (between min and max distance) */}
        <path
          d={`M ${cx - maxPx} ${screenY}
              A ${maxPx} ${maxPx} 0 0 0 ${cx + maxPx} ${screenY}
              L ${cx + minPx} ${screenY}
              A ${minPx} ${minPx} 0 0 1 ${cx - minPx} ${screenY}
              Z`}
          fill="#81B29A"
          opacity="0.22"
        />

        {/* Too-close arc (radius minPx) */}
        <path
          d={`M ${cx - minPx} ${screenY}
              A ${minPx} ${minPx} 0 0 0 ${cx + minPx} ${screenY}
              Z`}
          fill="#E07A5F"
          opacity="0.2"
        />

        {/* Off-axis 30° cone — lines */}
        <line
          x1={cx}
          y1={screenY}
          x2={cone30L.x}
          y2={cone30L.y}
          stroke="#E07A5F"
          strokeDasharray="6 4"
          strokeOpacity="0.6"
        />
        <line
          x1={cx}
          y1={screenY}
          x2={cone30R.x}
          y2={cone30R.y}
          stroke="#E07A5F"
          strokeDasharray="6 4"
          strokeOpacity="0.6"
        />
        <text x={cone30R.x + 4} y={cone30R.y - 4} fontSize="11" fill="#E07A5F">
          30° limit
        </text>

        {/* Max-legibility arc line */}
        <path
          d={`M ${cx - maxPx} ${screenY} A ${maxPx} ${maxPx} 0 0 0 ${cx + maxPx} ${screenY}`}
          fill="none"
          stroke="#1E2022"
          strokeOpacity="0.4"
          strokeDasharray="4 4"
        />
        <text x={cx + maxPx + 6} y={screenY + 4} fontSize="11" fill="hsl(var(--muted-foreground))">
          {calc.content?.multiplier}H max ({fromM(calc.maxDistM)} {unit})
        </text>

        {/* Min-distance arc line */}
        <path
          d={`M ${cx - minPx} ${screenY} A ${minPx} ${minPx} 0 0 0 ${cx + minPx} ${screenY}`}
          fill="none"
          stroke="#1E2022"
          strokeOpacity="0.4"
          strokeDasharray="3 3"
        />
        <text
          x={cx + minPx + 6}
          y={screenY + 22}
          fontSize="11"
          fill="hsl(var(--muted-foreground))"
        >
          Min ({fromM(calc.minDistM)} {unit})
        </text>

        {/* Audience seating block */}
        <rect
          x={front.x}
          y={front.y}
          width={back.x - front.x}
          height={back.y - front.y}
          fill="#D8E8DA"
          stroke="#1E2022"
          strokeOpacity="0.4"
          strokeWidth="1.5"
        />
        <text
          x={cx}
          y={(front.y + back.y) / 2 + 4}
          fontSize="12"
          fill="#1E2022"
          textAnchor="middle"
          opacity="0.7"
        >
          Audience
        </text>

        {/* Screen */}
        <rect
          x={cx - screenPx / 2}
          y={screenY - 10}
          width={screenPx}
          height={10}
          fill="#1E2022"
        />
        <text
          x={cx}
          y={screenY - 16}
          fontSize="12"
          fill="#1E2022"
          textAnchor="middle"
          fontWeight="600"
        >
          Screen ({fromM(calc.widthM)} {unit} wide)
        </text>

        {/* Centerline */}
        <line
          x1={cx}
          y1={screenY}
          x2={cx}
          y2={SVG_H - PAD}
          stroke="#1E2022"
          strokeOpacity="0.15"
          strokeDasharray="2 4"
        />
      </svg>
    </div>
  );
};

export default AVSpecs;
export { AVSpecs };
